import { useDictation } from "@/hooks/useDictation";
import * as React from "react";
import { Button } from "./ui/button";
import { Send, Mic, StopCircle, Grid2X2 } from "lucide-react";
import { Textarea } from "./ui/textarea";
import { Tooltip, TooltipContent, TooltipTrigger } from "./ui/tooltip";
import { useRequestGallery } from "./request-gallery/RequestGalleryContext";
import SoundWave from "./SoundWave";
import RequestGallery from "./request-gallery/RequestGallery";
import CompassLogo from "./CompassLogo";
import { useEffect, useRef, useState } from "react";
import { Command, CommandGroup, CommandItem, CommandList } from "./ui/command";
import { useClassifiedRequests } from "@/hooks/useClassifiedRequests";
import { Popover, PopoverContent, PopoverAnchor } from "./ui/popover";
import { cn } from "@/lib/utils";
import RequestCommandMenu from "./RequestCommandMenu";
import { Request } from "@/types/research.types";
import { CircularProgressBar } from "./ui/circular-progress-bar";

interface ChatInputWithGalleryProps {
  onSendMessage: (message: string) => Promise<void> | void;
  isSendingMessage: boolean;
  showCompassButton?: boolean;
  handleCompassClick?: () => void;
  compassAssetSrc?: string;
  requestType?: "discuss" | "research";
}

const ChatInputWithGallery: React.FC<ChatInputWithGalleryProps> = ({
  onSendMessage,
  isSendingMessage,
  showCompassButton = false,
  handleCompassClick,
  compassAssetSrc,
  requestType = "discuss",
}) => {
  const [chatMessage, setChatMessage] = useState("");
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { openGallery } = useRequestGallery();
  const [galleryId] = useState(() => Math.random().toString(36).substr(2, 9));

  const [activeMenu, setActiveMenu] = useState<"slash" | "hash" | null>(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedIndex, setSelectedIndex] = useState(0);
  const { simpleRequests, chainedRequests, requestMap } = useClassifiedRequests(requestType || "discuss");
  const [chainProgress, setChainProgress] = useState<{ current: number; total: number } | null>(null);

  // Filter requests based on search query
  const currentRequests = React.useMemo(() => {
    const list = activeMenu === "slash" ? simpleRequests : chainedRequests;
    if (!activeMenu || !searchQuery) return list;
    return list.filter((req) => req.name.toLowerCase().includes(searchQuery.toLowerCase()));
  }, [activeMenu, searchQuery, simpleRequests, chainedRequests]);

  const { isRecording, isMicroLoading, isConverting, startDictation, isSpeaking } = useDictation({
    onTranscript: setChatMessage,
  });

  useEffect(() => {
    console.log("[ChatInputDebug] State update:", { isRecording, isMicroLoading, isSpeaking });
  }, [isRecording, isMicroLoading, isSpeaking]);

  React.useEffect(() => {
    if (textareaRef.current) {
      const textarea = textareaRef.current;
      textarea.style.height = "auto";
      textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
    }
  }, [chatMessage, isSpeaking]);

  const handleMicClick = () => {
    startDictation();
  };

  const handleInternalTextareaChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
    const val = event.target.value;
    setChatMessage(val);

    // Regex to match trigger at start or after whitespace, capturing the trigger and following text
    // Group 1: Trigger (/ or #)
    // Group 2: Search query (anything not space, #, or /)
    const match = val.match(/(?:^|\s)([\/#])([^ #\/]*)$/);

    if (match) {
      const trigger = match[1];
      const query = match[2];

      if (trigger === "/") {
        setActiveMenu("slash");
      } else {
        setActiveMenu("hash");
      }
      setSearchQuery(query || "");
      setSelectedIndex(0); // Reset selection on filtering
    } else {
      setActiveMenu(null);
      setSearchQuery("");
      setSelectedIndex(0);
    }

    // Auto-resize textarea
    const textarea = event.target;
    textarea.style.height = "auto";
    textarea.style.height = `${Math.min(textarea.scrollHeight, 120)}px`;
  };

  const handleCommandSelect = (commandContent: string) => {
    // Determine trigger based on active menu
    const trigger = activeMenu === "slash" ? "/" : "#";
    const isSimpleRequest = activeMenu === "slash";

    if (isSimpleRequest) {
      onSendMessage(commandContent.trim());
      setChatMessage("");
      setActiveMenu(null);
      setSearchQuery("");
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
        textareaRef.current.style.height = "20px"; // Reset to minHeight
      }
      return;
    }

    // We assume the cursor is at the end or the match is at the end
    const suffix = `${trigger}${searchQuery}`;

    let newVal;
    if (chatMessage.endsWith(suffix)) {
      newVal = chatMessage.slice(0, -suffix.length) + commandContent;
    } else {
      // Fallback: This handles cases where regex matched but maybe something changed slightly,
      // though usually endsWith check covers simple typing.
      // If query was empty, it replaces just the trigger.
      newVal = chatMessage + commandContent;
    }

    setChatMessage(newVal + " "); // Add space for convenience
    setActiveMenu(null);
    setSearchQuery("");
    textareaRef.current?.focus();
    if (textareaRef.current) {
      // Auto resize after insertion
      setTimeout(() => {
        if (textareaRef.current) {
          textareaRef.current.style.height = "auto";
          textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
        }
      }, 0);
    }
  };

  const handleInternalSendMessage = () => {
    if (chatMessage.trim()) {
      onSendMessage(chatMessage.trim());
      setChatMessage("");
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
        textareaRef.current.style.height = "20px"; // Reset to minHeight
      }
    }
  };

  const handleInternalKeyPress = (e: React.KeyboardEvent) => {
    if (activeMenu) {
      // Use filtered list
      const currentList = currentRequests;

      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((prev) => Math.min(prev + 1, currentList.length - 1));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((prev) => Math.max(prev - 1, 0));
      } else if (e.key === "Enter") {
        e.preventDefault();
        if (currentList.length > 0 && currentList[selectedIndex]) {
          handleCommandSelect(currentList[selectedIndex].content);
        }
      } else if (e.key === "Escape") {
        e.preventDefault();
        setActiveMenu(null);
      }
      return;
    }

    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleInternalSendMessage();
    }
  };

  const handleInternalRequestSelect = async (content: string, request?: Request) => {
    if (request?.chain_with && request.chain_with.length > 0) {
      const fullChain = [
        request.content,
        ...request.chain_with.map(id => requestMap[id]?.content).filter(Boolean)
      ];
      setChainProgress({ current: 0, total: fullChain.length });

      for (let i = 0; i < fullChain.length; i++) {
        const msg = fullChain[i];
        if (msg) {
          setChainProgress({ current: i + 1, total: fullChain.length });
          await onSendMessage(msg.replace(/\s+/g, " ").trim());
        }
      }

      // Delay to show final progress before hiding
      setTimeout(() => setChainProgress(null), 2000);
    } else {
      setChatMessage(content.replace(/\s+/g, " ").trim()); // Populate the input with the request text
      if (textareaRef.current) {
        textareaRef.current.style.height = "auto";
        textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`; // Auto-resize
      }
    }
  };

  const handleOpenRequestGallery = () => {
    openGallery(galleryId, {
      type: requestType,
      onSelect: handleInternalRequestSelect,
    });
  };

  // currentRequests is now defined above
  // Fallback to empty string if no request is selected (shouldn't happen with 0 index default but safe)
  const selectedId = currentRequests[selectedIndex]?.id || "";

  return (
    <Popover
      open={!!activeMenu}
      onOpenChange={(open) => {
        if (!open) setActiveMenu(null);
      }}
    >
      <div className={`flex items-end gap-2 mt-3 ${isSendingMessage ? "cursor-not-allowed" : ""}`}>
        <PopoverAnchor asChild>
          <div className="relative flex-1">
            {chainProgress && (
              <Tooltip open={true}>
                <TooltipTrigger asChild>
                  <div className="absolute -top-[72px] left-0.5 z-20">
                    <CircularProgressBar
                      progress={(chainProgress.current / chainProgress.total) * 100}
                      size={8}
                      className="bg-neutral-white rounded-full shadow-sm border border-primary-200"
                    >
                      <span className="text-[10px] font-bold text-primary-300">
                        {chainProgress.current}
                      </span>
                    </CircularProgressBar>
                  </div>
                </TooltipTrigger>
                <TooltipContent side="top" align="start">
                  <p className="text-[10px]">Action {chainProgress.current} of {chainProgress.total}</p>
                </TooltipContent>
              </Tooltip>
            )}
            <div className="relative items-center pr-1.5 min-h-10 flex border rounded-md overflow-hidden animate-border-pulse focus-within:animate-none transition-shadow duration-300">
              <RequestGallery id={galleryId} type={requestType} tooltip="Request Gallery">
                <Button
                  className="h-8 w-8 p-0 ml-1 rounded-full bg-primary-300 text-neutral-white hover:bg-primary-400 shrink-0"
                  onClick={handleOpenRequestGallery}
                >
                  <Grid2X2 className="size-5" />
                </Button>
              </RequestGallery>
              {(isRecording || isMicroLoading) && isSpeaking ? (
                <div className="flex-1 flex items-center justify-center h-10">
                  <SoundWave />
                </div>
              ) : (
                <Textarea
                  ref={textareaRef}
                  className="flex-1 border-none focus:ring-0 focus-visible:ring-0 p-0 outline-none resize-none pl-2 bg-transparent text-sm min-h-5 shadow-none py-1.5 placeholder:text-neutral-600"
                  placeholder="Type or dictate..."
                  value={chatMessage}
                  onChange={handleInternalTextareaChange}
                  onKeyDown={handleInternalKeyPress}
                  rows={1}
                  disabled={isSendingMessage}
                />
              )}

              {showCompassButton && handleCompassClick && compassAssetSrc && (
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      className="h-auto w-auto p-0 ml-1 py-1.5 rounded-full"
                      variant="ghost"
                      onClick={handleCompassClick}
                    >
                      <CompassLogo src={compassAssetSrc} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent>
                    <p>Click here to start work.</p>
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          </div>
        </PopoverAnchor>
        <PopoverContent
          className="w-[calc(100%-80px)] min-w-[350px] max-w-[500px] p-0 mb-2"
          onOpenAutoFocus={(e) => e.preventDefault()}
          onInteractOutside={(e) => {
            if (textareaRef.current && textareaRef.current.contains(e.target as Node)) {
              e.preventDefault();
            }
          }}
          align="start"
          side="top"
        >
          <RequestCommandMenu
            heading={activeMenu === "slash" ? "Simple Requests" : "Chained Requests"}
            requests={currentRequests}
            onSelect={handleCommandSelect}
            selectedId={selectedId}
          />
        </PopoverContent>

        <div className="flex gap-2 items-center">
          {isRecording || isMicroLoading ? (
            <Button
              size="icon"
              variant="destructive"
              className="size-10 p-0"
              onClick={handleMicClick}
              title="Stop recording"
              disabled={isSendingMessage}
            >
              <StopCircle />
            </Button>
          ) : chatMessage.trim() === "" ? (
            <Button
              size="icon"
              variant="default"
              className="size-10 p-0"
              onClick={handleMicClick}
              disabled={isMicroLoading || isConverting || isSendingMessage}
              title="Voice message"
            >
              <Mic />
            </Button>
          ) : (
            <Button
              size="icon"
              className="size-10 p-0"
              onClick={handleInternalSendMessage}
              disabled={!chatMessage.trim() || isSendingMessage}
              title="Send message"
            >
              <Send />
            </Button>
          )}
        </div>
      </div>
    </Popover>
  );
};

export default ChatInputWithGallery;
